import type { RefObject } from 'react';
import type React from 'react';

import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Separator, SocialSharing } from '@change/ds-core-ui';
import { ArrowLeftIcon, QrCodeIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import type { I18nUtilities } from '@change-corgi/core-react/i18n';
import { useI18n } from '@change-corgi/core-react/i18n';

import type { ChannelAwareCta } from '#src/app/pages/psf/promoteOrShare/shared/hooks';
import { RoundShareIconButton, ShareChannelButton } from '#src/app/shared/components/share';

import { ChannelAwareCtaButton } from './components/ChannelAwareCtaButton';
import { PetitionShareSlidePetitionCard } from './components/PetitionShareSlideUpHeader';
import { QrCodeView } from './components/QrCodeView';
import {
	ACTION_BUTTON_TRACKING_DATA,
	CHANNEL_GRID_TRACKING_DATA,
	getShareIconOnBeforeShare,
	SHARE_ICON_SIZE_CLASSNAME,
} from './constants';
import { useQrCodeSteps } from './hooks';
import type { PetitionShareButtonConfig, PetitionShareSlideUpProps } from './types';

// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
// ModalHeader overrides the leading slot's `size` and `className`, so the back button's styles, focus
// ring included, go through `leadingSlotClassName`.
const BACK_BUTTON_CLASSNAME = cn(
	'p-0 no-underline',
	'focus-visible:outline-stroke-focus focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-solid',
);

/**
 * EXPERIMENT: psf_promote_or_share_qr_code_experiment.
 * Not a share channel, so it renders the DS button directly. The `contents` wrapper gives `tileRef`
 * an element to find the button in.
 */
const QrCodeTile = ({ onPress, tileRef }: { onPress: () => void; tileRef: RefObject<HTMLDivElement | null> }) => {
	const { translate } = useI18n();
	return (
		<div ref={tileRef} className="contents" data-testid="qr-code-tile">
			<SocialSharing
				icon={<QrCodeIcon />}
				label={translate('corgi.psf.promote_or_share.share_slide_up.qr_code.tile_label')}
				variant="default"
				className={cn(SHARE_ICON_SIZE_CLASSNAME, 'w-full')}
				onClick={onPress}
				data-qa="petition-share-slide-up-grid-qr-code-button"
			/>
		</div>
	);
};

/** A plain function, not a component, so the Modal still finds its ModalHeader child. */
function renderHeader({
	shareVisual,
	onBack,
	translate,
}: {
	shareVisual: string | undefined;
	/** Set only on the QR view. */
	onBack: (() => void) | null;
	translate: I18nUtilities['translate'];
}) {
	if (!onBack) {
		return (
			<ModalHeader
				title={shareVisual ? translate('corgi.psf.promote_or_share.share_slide_up.aria_label') : undefined}
			/>
		);
	}
	return (
		// The design's QR view header has only back and close, no title.
		<ModalHeader
			leadingSlotClassName={BACK_BUTTON_CLASSNAME}
			leadingSlot={
				<Button
					iconOnly
					variant="tertiary"
					leadingIcon={<ArrowLeftIcon size="medium" />}
					aria-label={translate('corgi.psf.promote_or_share.share_slide_up.qr_code.back_button')}
					onClick={onBack}
					data-testid="qr-code-view-back-button"
				/>
			}
		/>
	);
}

const SpecialShareButtons = ({ configs }: { configs: readonly PetitionShareButtonConfig[] }) => (
	<div className="grid grid-cols-1 gap-x-0.5 gap-y-2" data-testid="petition-share-slide-up-special-buttons">
		{configs.map((buttonConfig) => (
			<ShareChannelButton
				key={buttonConfig.channel}
				buttonConfig={buttonConfig}
				trackingData={ACTION_BUTTON_TRACKING_DATA}
			/>
		))}
	</div>
);

/**
 * Splits the server-provided configs into the top "special" buttons and the remaining grid channels.
 * With a channel-aware CTA (productized per-country via the `psf_promote_or_share_channel_aware_cta`
 * FCM) a "Share on <channel>" button is promoted above the grid while the channel — and Copy link —
 * STAY in the grid. When no CTA renders (no channel-aware CTA, or its channel is missing from the share
 * config) Copy link is the primary special button, so the sheet still has a prominent action.
 */
function splitButtonConfigs(
	buttonConfigs: readonly PetitionShareButtonConfig[],
	channelAwareCta: ChannelAwareCta,
): {
	ctaButtonConfig: PetitionShareButtonConfig | undefined;
	specialButtonConfigs: PetitionShareButtonConfig[];
	regularButtonConfigs: PetitionShareButtonConfig[];
} {
	const ctaButtonConfig = channelAwareCta
		? buttonConfigs.find((config) => config.channel === channelAwareCta.channel)
		: undefined;
	const keepCopyInGrid = ctaButtonConfig !== undefined;
	const specialButtonConfigs: PetitionShareButtonConfig[] = [];
	const regularButtonConfigs = buttonConfigs.filter((config) => {
		if (config.channel === 'copy' && !keepCopyInGrid) {
			specialButtonConfigs.push(config);
			return false;
		}
		return true;
	});
	return { ctaButtonConfig, specialButtonConfigs, regularButtonConfigs };
}

const Footer = ({ continueButtonProps }: { continueButtonProps: PetitionShareSlideUpProps['continueButtonProps'] }) => {
	if (!continueButtonProps) return null;

	return (
		<ModalFooter className="justify-center" data-testid="petition-share-slide-up-footer" displayFooterTopSeparator>
			<Button
				data-qa="petition-share-slide-up-continue-button"
				variant="tertiary"
				className="w-full"
				onClick={continueButtonProps.action}
			>
				{continueButtonProps.label}
			</Button>
		</ModalFooter>
	);
};

/**
 * Extracted only to keep PetitionShareSlideUp's render within the max-lines-per-function budget.
 */
const ShareChannelsGrid = ({
	buttonConfigs,
	close,
	closeOnSocialShare,
	trailingTile,
}: {
	buttonConfigs: readonly PetitionShareButtonConfig[];
	close: () => void;
	closeOnSocialShare: boolean;
	/** A non-channel tile after the channel tiles. */
	trailingTile?: React.ReactNode;
}) => (
	<div className="grid grid-cols-4 gap-x-0.5 gap-y-5" data-testid="petition-share-slide-up-channels-grid">
		{buttonConfigs.map((buttonConfig) => (
			<RoundShareIconButton
				key={buttonConfig.channel}
				className={cn(SHARE_ICON_SIZE_CLASSNAME, 'w-full')}
				buttonConfig={buttonConfig}
				data-qa={`petition-share-slide-up-grid-${buttonConfig.channel}-button`}
				trackingData={CHANNEL_GRID_TRACKING_DATA}
				onBeforeShare={getShareIconOnBeforeShare({ channel: buttonConfig.channel, close, closeOnSocialShare })}
			/>
		))}
		{trailingTile}
	</div>
);

// eslint-disable-next-line max-lines-per-function
export const PetitionShareSlideUp: React.FC<PetitionShareSlideUpProps> = ({
	petition,
	buttonConfigs,
	onClose = () => {},
	onOpenChange,
	continueButtonProps,
	isOpen,
	shareVisual,
	closeOnSocialShare = true,
	channelAwareCta = null,
	qrCodeLayout,
	qrCode,
}) => {
	const { translate } = useI18n();
	// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
	const { showQrTile, qrView, qrTileRef, openQrView, showGrid, openPosterView } = useQrCodeSteps(
		isOpen,
		qrCodeLayout,
		qrCode,
	);
	const showsShareVisual = Boolean(shareVisual) && !qrView;

	const { ctaButtonConfig, specialButtonConfigs, regularButtonConfigs } = splitButtonConfigs(
		buttonConfigs,
		channelAwareCta,
	);

	return (
		<Modal
			isOpen={isOpen}
			transformToSheet
			onClose={onClose}
			onOpenChange={onOpenChange}
			aria-label={translate('corgi.psf.promote_or_share.share_slide_up.aria_label')}
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
			className="z-100"
		>
			{renderHeader({ shareVisual, onBack: qrView && showGrid, translate })}

			{showsShareVisual && (
				<Separator className="border-stroke-weaker -mx-5 mt-4 mb-0 w-[calc(100%+2.5rem)] md:-mx-6 md:w-[calc(100%+3rem)]" />
			)}

			<ModalBody
				// The QR view drops the height cap so the sheet grows to fit it instead of scrolling.
				className={cn('pb-6', { 'max-h-[calc(85dvh-80px)]': !qrView, 'pt-0': showsShareVisual })}
				data-testid="petition-share-slide-up-body"
			>
				{({ close }) =>
					qrView ? (
						<QrCodeView
							qrCode={qrView.qrCode}
							petitionTitle={petition.ask}
							showPrint={qrView.showPrint}
							onOpenPoster={openPosterView}
						/>
					) : (
						<div className="w-full">
							{shareVisual ? (
								<img src={shareVisual} alt="Share Visual Preview" className="mx-auto my-4 block max-w-61 rounded-md" />
							) : (
								<div className="mb-4">
									<PetitionShareSlidePetitionCard petition={petition} />
								</div>
							)}
							{(ctaButtonConfig || specialButtonConfigs.length > 0) && (
								<>
									<div className="flex flex-col gap-4">
										{channelAwareCta && ctaButtonConfig && (
											<ChannelAwareCtaButton
												channel={channelAwareCta.channel}
												buttonConfig={ctaButtonConfig}
												onBeforeShare={getShareIconOnBeforeShare({
													channel: channelAwareCta.channel,
													close,
													closeOnSocialShare,
												})}
											/>
										)}
										{specialButtonConfigs.length > 0 && <SpecialShareButtons configs={specialButtonConfigs} />}
									</div>
									<Separator
										className="border-stroke-weaker my-5"
										data-testid="petition-share-slide-up-top-separator"
									/>
								</>
							)}
							<ShareChannelsGrid
								buttonConfigs={regularButtonConfigs}
								close={close}
								closeOnSocialShare={closeOnSocialShare}
								trailingTile={showQrTile && <QrCodeTile onPress={openQrView} tileRef={qrTileRef} />}
							/>
						</div>
					)
				}
			</ModalBody>
			{!qrView && <Footer continueButtonProps={continueButtonProps} />}
		</Modal>
	);
};
